import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Snackbar, Alert, Box, Typography } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { getMessages } from '../service/messaging';

/**
 * GlobalChatNotifier - Polls for new counsellor messages on any page.
 * Shows browser notification + in-app toast + sound when a new message arrives.
 * Clicking the toast navigates to /converse.
 */
const GlobalChatNotifier = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const lastMessageCountRef = useRef(null);
	const titleIntervalRef = useRef(null);
	const originalTitleRef = useRef(document.title);

	// Play notification sound
	const playNotificationSound = useCallback(() => {
		try {
			const audioContext = new (window.AudioContext || window.webkitAudioContext)();
			const oscillator = audioContext.createOscillator();
			const gainNode = audioContext.createGain();

			oscillator.connect(gainNode);
			gainNode.connect(audioContext.destination);

			oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
			oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1);
			oscillator.type = 'sine';

			gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
			gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);

			oscillator.start(audioContext.currentTime);
			oscillator.stop(audioContext.currentTime + 0.4);
		} catch (e) {
			console.log('Could not play notification sound:', e);
		}
	}, []);

	// Show browser notification
	const showBrowserNotification = useCallback((message) => {
		if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
			const notification = new Notification('New message from Counsellor', {
				body: message,
				icon: '/logo.png',
				tag: 'echoshine-global-chat',
			});
			notification.onclick = () => {
				window.focus();
				notification.close();
				navigate('/converse');
			};
			setTimeout(() => notification.close(), 5000);
		}
	}, [navigate]);

	// Flash browser tab title
	const flashTitle = useCallback(() => {
		if (titleIntervalRef.current) return;
		const original = document.title;
		originalTitleRef.current = original;
		let toggle = false;
		titleIntervalRef.current = setInterval(() => {
			document.title = toggle ? '💬 New message!' : original;
			toggle = !toggle;
		}, 1000);

		const stopFlash = () => {
			clearInterval(titleIntervalRef.current);
			titleIntervalRef.current = null;
			document.title = originalTitleRef.current;
			window.removeEventListener('focus', stopFlash);
		};
		window.addEventListener('focus', stopFlash);
	}, []);

	// Request notification permission
	useEffect(() => {
		if ('Notification' in window && Notification.permission === 'default') {
			Notification.requestPermission();
		}
	}, []);

	// Stop title flashing when user focuses window
	useEffect(() => {
		const handleFocus = () => {
			if (titleIntervalRef.current) {
				clearInterval(titleIntervalRef.current);
				titleIntervalRef.current = null;
				document.title = originalTitleRef.current;
			}
		};
		window.addEventListener('focus', handleFocus);
		return () => window.removeEventListener('focus', handleFocus);
	}, []);

	// Global polling - check for new messages every 5 seconds
	useEffect(() => {
		// Skip polling if on the converse page (it has its own real-time handling)
		const isOnConversePage = location.pathname.startsWith('/converse');
		if (isOnConversePage) {
			// Reset baseline when entering converse page so it re-calibrates on exit
			lastMessageCountRef.current = null;
			return;
		}

		const pollForNewMessages = async () => {
			// Read active conversation from localStorage (set by converse page)
			const conversationId = localStorage.getItem('activeConversationId');
			if (!conversationId) return;

			try {
				const messagesData = await getMessages(conversationId);
				const messages = messagesData.messages || [];
				const currentCount = messages.length;

				// First time: just store baseline, don't notify
				if (lastMessageCountRef.current === null) {
					lastMessageCountRef.current = currentCount;
					console.log('🔔 Global notifier baseline set:', currentCount, 'messages');
					return;
				}

				// New messages detected
				if (currentCount > lastMessageCountRef.current) {
					const newMessages = messages.slice(lastMessageCountRef.current);
					const counsellorMessages = newMessages.filter(m => m.sender_type !== 'customer');

					if (counsellorMessages.length > 0) {
						const latestMsg = counsellorMessages[counsellorMessages.length - 1].message_text;
						console.log('🔔 Global notification: new counsellor message:', latestMsg);
						playNotificationSound();
						showBrowserNotification(latestMsg);
						flashTitle();
						setAlertMessage(latestMsg);
						setAlertOpen(true);
					}

					lastMessageCountRef.current = currentCount;
				}
			} catch (err) {
				console.debug('Global chat poll error:', err);
			}
		};

		// Run immediately on first mount (to set baseline)
		pollForNewMessages();

		const pollInterval = setInterval(pollForNewMessages, 5000);
		return () => clearInterval(pollInterval);
	}, [location.pathname, playNotificationSound, showBrowserNotification, flashTitle]);

	const handleAlertClick = () => {
		setAlertOpen(false);
		navigate('/converse');
	};

	return (
		<Snackbar
			open={alertOpen}
			autoHideDuration={6000}
			onClose={() => setAlertOpen(false)}
			anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
			onClick={handleAlertClick}
			sx={{ cursor: 'pointer', zIndex: 9999 }}
		>
			<Alert
				onClose={() => setAlertOpen(false)}
				severity="info"
				variant="filled"
				icon={<ChatIcon />}
				sx={{
					background: 'linear-gradient(135deg, #22c55e, #14b8a6)',
					color: '#fff',
					fontWeight: 600,
					fontSize: 13,
					borderRadius: '12px',
					boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
					cursor: 'pointer',
					'&:hover': { opacity: 0.95 },
				}}
			>
				<Box>
					<Typography sx={{ fontWeight: 700, fontSize: 13 }}>
						Counsellor replied
					</Typography>
					<Typography sx={{ fontSize: 12, opacity: 0.9 }}>
						{alertMessage?.length > 60 ? alertMessage.slice(0, 60) + '...' : alertMessage}
					</Typography>
					<Typography sx={{ fontSize: 11, opacity: 0.75, mt: 0.5 }}>
						Tap to open chat
					</Typography>
				</Box>
			</Alert>
		</Snackbar>
	);
};

export default GlobalChatNotifier;
